// @flow

/**
 * AI Chat Questions Registry & Builder
 *
 * Centralised source of truth for all AI chat intro questions.
 * Questions are categorised to allow filtering based on feature flags
 * and available metric groups.
 */
const MAX_QUESTIONS = 5;

export type QuestionCategory = 'general' | 'staff' | 'demographic';

export type MetricCategory = 'any' | 'movement' | 'movementWithFilters' | 'zones' | 'serviceAreas' | 'sales';

export type Question = {
  text: string,
  questionCategory: QuestionCategory,
  oneOfRequiredMetricCategory: MetricCategory[],
};

export type QuestionFilterOptions = {
  staffDetectionDisabled: boolean,
  demographicsDisabled: boolean,
  availableMetricGroups: string[],
};

// ============================================================================
// AI Chat Questions (main chat interface)
// ============================================================================

export const AI_CHAT_QUESTIONS: Question[] = [
  { text: 'How can I improve store performance?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'What should I focus on this week?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Which stores had the largest queues?', questionCategory: 'general', oneOfRequiredMetricCategory: ['zones', 'serviceAreas'] },
  { text: 'What demographic trends did we see last week?', questionCategory: 'demographic', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Which product areas have the highest engagement?', questionCategory: 'general', oneOfRequiredMetricCategory: ['zones', 'serviceAreas'] },
  { text: 'What are our peak trading times?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Did we have a strong weekend?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Was last week\'s marketing campaign successful?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
];

export const AI_CHAT_INTRO_MESSAGE = "Hi there I'm Aura, your in-store retail analyst 👋. Ask me questions like:";

// ============================================================================
// Report Chat Questions (analysing existing reports)
// ============================================================================

export const REPORT_CHAT_QUESTIONS: Question[] = [
  { text: 'Summarise the most important insights', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Identify top trends', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Why are these metrics important?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Drill into this time period in more detail', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'What should I add to this report?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
];

export const REPORT_CHAT_INTRO_MESSAGE = 'I can analyse this report for you. Ask me questions like:';

// ============================================================================
// Report Designer Questions (creating/editing reports)
// ============================================================================

export const REPORT_DESIGNER_CREATE_QUESTIONS: Question[] = [
  { text: 'Store manager report', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Store performance report for London stores', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Peak time report for service areas', questionCategory: 'general', oneOfRequiredMetricCategory: ['serviceAreas'] },
  { text: 'Summarise engagement across product areas', questionCategory: 'general', oneOfRequiredMetricCategory: ['zones', 'serviceAreas'] },
  { text: "I'm in the marketing team, can you recommend a report?", questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Peak trading hour report', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Breakdown of traffic across all stores week on week', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Weekend Capture rate report', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
];

export const REPORT_DESIGNER_CREATE_INTRO_MESSAGE = 'I can build reports so you can set up regular email schedules and share with your organisation. Try asking me for a:';

export const REPORT_DESIGNER_EDIT_QUESTIONS: Question[] = [
  { text: 'Make this report shorter', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Expand on this report', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'What graphs do you recommend for this report?', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Drill into this time period in more detail', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
  { text: 'Add a line chart that shows movement by week', questionCategory: 'general', oneOfRequiredMetricCategory: ['any'] },
];

export const REPORT_DESIGNER_EDIT_INTRO_MESSAGE = 'I can edit this report for you. Ask me questions like:';

// ============================================================================
// Builder Function
// ============================================================================

/**
 * Filters questions based on feature flags and available metric groups,
 * then builds the intro text.
 *
 * @param questions - Array of questions to filter
 * @param introMessage - The intro message to display before questions
 * @param options - Feature flag options and available metric groups
 * @returns Formatted intro text with filtered questions wrapped in <prompt> tags
 */
export function buildIntroText(
  questions: Question[],
  introMessage: string,
  options: QuestionFilterOptions = { staffDetectionDisabled: false, demographicsDisabled: false, availableMetricGroups: [] }
): string {
  const { staffDetectionDisabled, demographicsDisabled, availableMetricGroups } = options;

  const filteredQuestions = questions.filter((q) => {
    if (q.questionCategory === 'staff' && staffDetectionDisabled) return false;
    if (q.questionCategory === 'demographic' && demographicsDisabled) return false;

    if (q.oneOfRequiredMetricCategory.includes('any')) return true;

    return q.oneOfRequiredMetricCategory.some(
      (mc) => availableMetricGroups.includes(mc)
    );
  });

  const selected = filteredQuestions.length <= MAX_QUESTIONS
    ? filteredQuestions
    : (() => {
        const pool = [...filteredQuestions];
        const picked = [];
        while (picked.length < MAX_QUESTIONS) {
          const idx = Math.floor(Math.random() * pool.length);
          picked.push(pool.splice(idx, 1)[0]);
        }
        return picked;
      })();

  const promptLines = selected
    .map((q) => `<prompt>${q.text}</prompt>`)
    .join('\n\n');

  return `${introMessage}\n\n${promptLines}\n`;
}

// ============================================================================
// Convenience Builders (pre-configured for each context)
// ============================================================================

export function buildAIChatIntroText(options: QuestionFilterOptions): string {
  return buildIntroText(AI_CHAT_QUESTIONS, AI_CHAT_INTRO_MESSAGE, options);
}

export function buildReportChatIntroText(options: QuestionFilterOptions): string {
  return buildIntroText(REPORT_CHAT_QUESTIONS, REPORT_CHAT_INTRO_MESSAGE, options);
}

export function buildReportDesignerCreateIntroText(options: QuestionFilterOptions): string {
  return buildIntroText(REPORT_DESIGNER_CREATE_QUESTIONS, REPORT_DESIGNER_CREATE_INTRO_MESSAGE, options);
}

export function buildReportDesignerEditIntroText(options: QuestionFilterOptions): string {
  return buildIntroText(REPORT_DESIGNER_EDIT_QUESTIONS, REPORT_DESIGNER_EDIT_INTRO_MESSAGE, options);
}
